/**
 * ========================================
 * BOOK DEMO MODAL
 * ========================================
 * Public, unauthenticated "Book a Live Demo" form opened from the landing
 * page (features/demo.md). Single-wrapper-div modal structure (overlay +
 * centering container as one element, dialog as its child) — matches
 * ProfileSettingsModal.tsx / SupportTicketModal.tsx, not ConfirmationModal's
 * separate-backdrop-sibling pattern (which caused a real z-index stacking
 * bug previously).
 */

import React, { useState } from 'react';
import { demoRequestService } from '@services/api/demo-request.service';
import { getErrorMessage } from '@services/api';
import { STANDARD_OPTIONS, BOARD_OPTIONS, INDIAN_STATES, standardOrdinal } from '@/types/demoRequest';
import type { Board, Standard } from '@/types/demoRequest';

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opened from a "Book a school demo" CTA: the booking isn't for one student's grade,
   * so Standard is shown as a fixed "1st – 10th" label instead of an editable dropdown,
   * and submitted as the school-wide "ALL" value (see types/demoRequest.ts). */
  isSchool?: boolean;
}

interface DemoFormData {
  email: string;
  phone: string;
  standard: Standard | '';
  board: Board | '';
  state: string;
  preferred_date: string;
  preferred_time: string;
}

const EMPTY_FORM: DemoFormData = {
  email: '', phone: '', standard: '', board: '', state: '',
  preferred_date: '', preferred_time: '',
};

const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/;

export const BookDemoModal: React.FC<BookDemoModalProps> = ({ isOpen, onClose, isSchool = false }) => {
  const [formData, setFormData] = useState<DemoFormData>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Re-seed the form on every open, since this component stays mounted between opens (parent
  // toggles `isOpen`, not the JSX). School bookings aren't for one grade, so Standard is
  // pre-set to "ALL" and never shown as an editable dropdown (see JSX below).
  React.useEffect(() => {
    if (isOpen) {
      setFormData(isSchool ? { ...EMPTY_FORM, standard: 'ALL' } : EMPTY_FORM);
      setErrorMessage('');
      setSubmitSuccess(false);
    }
  }, [isOpen, isSchool]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Clear state whenever board changes away from "State Board"
      ...(name === 'board' && value !== 'State Board' ? { state: '' } : {}),
    }));
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setErrorMessage('');
    setSubmitSuccess(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.standard || !formData.board) {
      setErrorMessage('Please select a standard and board.');
      return;
    }
    if (!PHONE_PATTERN.test(formData.phone)) {
      setErrorMessage('Please enter a valid phone number (with country code, e.g. +919999999999).');
      return;
    }
    if (formData.board === 'State Board' && !formData.state) {
      setErrorMessage('Please select your state.');
      return;
    }

    setIsSubmitting(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const result = await demoRequestService.create({
        email: formData.email,
        phone: formData.phone,
        standard: formData.standard,
        board: formData.board,
        state: formData.board === 'State Board' ? formData.state : undefined,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        timezone,
      });

      setSuccessMessage(result.message);
      setSubmitSuccess(true);
      setTimeout(handleClose, 3000);
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error) || 'Failed to submit demo request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg z-10">
          <div>
            <h2 className="font-bold text-lg text-gray-900">{isSchool ? 'Book a School Demo' : 'Book a Live Demo'}</h2>
            <p className="text-xs text-gray-500">
              {isSchool
                ? 'See how the AI Tutor platform works across every grade your school teaches.'
                : 'Experience the AI Tutor platform with a personalized demonstration.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {submitSuccess ? (
          <div className="p-8 flex flex-col items-center justify-center gap-4 min-h-[300px]">
            <div className="text-6xl animate-bounce">✅</div>
            <h3 className="text-xl font-bold text-gray-900 text-center">Demo request submitted successfully.</h3>
            <p className="text-gray-500 text-center">{successMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {errorMessage && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{errorMessage}</div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+919999999999"
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Standard *</label>
                {isSchool ? (
                  <div
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-600"
                    aria-readonly="true"
                    title="A school demo covers every grade, so this isn't editable."
                  >
                    1st – 10th
                  </div>
                ) : (
                  <select
                    name="standard"
                    value={formData.standard}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                  >
                    <option value="">Select</option>
                    {STANDARD_OPTIONS.map((s) => <option key={s} value={s}>{standardOrdinal(s)}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Board *</label>
                <select
                  name="board"
                  value={formData.board}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="">Select</option>
                  {BOARD_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>

            {formData.board === 'State Board' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                >
                  <option value="">Select your state</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Date *</label>
                <input
                  type="date"
                  name="preferred_date"
                  value={formData.preferred_date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().slice(0, 10)}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Time *</label>
                <input
                  type="time"
                  name="preferred_time"
                  value={formData.preferred_time}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-indigo-600 text-white px-4 py-2.5 rounded-lg font-semibold disabled:opacity-50 hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">⚙️</span>
                  Submitting...
                </>
              ) : (
                isSchool ? 'Book School Demo' : 'Book Live Demo'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
